import { checkNullString } from '@/pages/datalab/utils/utils';
import { DBColumnType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { FieldTypeDefaultAccuracy } from '@dbl/core/chartEditor';
import { getDefaultNumberUnit } from '@dbl/core/chartEditor';

/**
 * 指标区写回 model：拖拽 setList、close 删除、批量下拉。
 * 列排序由 ChartSettingsV2 useQBLinkagePipeline 在 QB 变更后维护。
 */

export interface IMetricSortablePersistOptions {
  sortableKey: string;
  chartType: string;
  dispatchFun: TDispatchFun;
  /** 局部 save 时透传，避免覆盖 save_column_infos 其它字段 */
  saveColumnInfos?: Record<string, unknown>;
}

export interface IMetricSortablePersist {
  saveList: (list: IColumnInfo[]) => void;
  removeItems: (currentList: IColumnInfo[], removedItems: IColumnInfo[]) => void;
  applyBatchChange: (nextMetrics: IColumnInfo[]) => void;
}

/** 与 metricSortable.setList / 批量新增一致的 transform */
export const normalizeMetricField = (
  item: IColumnInfo,
  chartType: string,
): IColumnInfo => ({
  aggregate: item.is_aggregate ? '' : DBColumnType.getMetricAggregate(item),
  ...item,
  accuracy: item.accuracy ?? FieldTypeDefaultAccuracy[item.column_type],
  unit: (item.unit ??
    getDefaultNumberUnit({ chartType, fieldType: item.column_type })) as IColumnInfo['unit'],
});

export const createMetricSortablePersist = (
  options: IMetricSortablePersistOptions,
): IMetricSortablePersist => {
  const { sortableKey, chartType, dispatchFun, saveColumnInfos } = options;

  const normalizeList = (list: IColumnInfo[]): IColumnInfo[] =>
    list
      .filter(item => !checkNullString(item.id))
      .map(item => normalizeMetricField(item, chartType));

  const persistMetrics = (nextMetrics: IColumnInfo[]): void => {
    const payload: Record<string, unknown> = {
      [sortableKey]: nextMetrics,
    };
    if (saveColumnInfos) {
      payload.saveColumnInfos = saveColumnInfos;
    }
    dispatchFun('save', payload);
  };

  const saveList = (list: IColumnInfo[]): void => {
    persistMetrics(normalizeList(list));
  };

  const removeItems = (
    currentList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (!removedItems.length) {
      return;
    }
    const removedKeySet = new Set(
      removedItems.map(item => `${item.id}_${item.table_code}`),
    );
    const nextMetrics = currentList.filter(
      item =>
        !checkNullString(item.id) &&
        !removedKeySet.has(`${item.id}_${item.table_code}`),
    );
    persistMetrics(nextMetrics);
  };

  const applyBatchChange = (nextMetrics: IColumnInfo[]): void => {
    persistMetrics(normalizeList(nextMetrics));
  };

  return {
    saveList,
    removeItems,
    applyBatchChange,
  };
};
